<script setup lang="ts">
  import { reactive, computed, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { Dialog, Button, IconWarningRound, Text, useNotification } from "@ui-kit";
  import { ResponseCode, type Item, itemLoadApi, itemLoadByGuidApi, itemUnsetZoneApi, itemSetZoneApi } from "@src/api";
  import { ProductZone } from "@src/components";
  import { useShopZoneHook } from "@src/hooks";

  const { item_id, zone_id } = defineProps<{ item_id: string; zone_id: string }>();
  const emit = defineEmits<{ "close": [value: void]; "click:link": [value: void] }>();

  const { shopZones } = useShopZoneHook();
  const notification = useNotification();
  const { t } = useI18n();

  const state = reactive<{ item: Item; zone_ids: string[]; is_pending_relink: boolean; is_pending_link: boolean }>({
    item: null,
    zone_ids: [],
    is_pending_relink: false,
    is_pending_link: false,
  });
  const item = computed(() => {
    const item: Item & { zones?: { id: string; path: string; location: string }[] } = { ...state.item, zones: [] };

    if (!item) return null;

    const zones = state.zone_ids.map((id) => shopZones.value.byId[id]);

    zones.filter(Boolean).forEach((zone) => {
      const zone_ids = zone.hierarchy.split(".");
      let path = "";
      let location = "";

      if (zone_ids.length === 2) {
        path = shopZones.value.byId[zone_ids.at(0)].title;
        location = shopZones.value.byId[zone_ids.at(-1)].title;
      } else if (zone_ids.length > 2) {
        const path_origin = [
          shopZones.value.byId[zone_ids.at(0)].title.slice(0, 1),
          shopZones.value.byId[zone_ids.at(1)].title.slice(-2),
        ].join(" ");
        const path_other = zone_ids.slice(2, -1).map((id) => shopZones.value.byId[id].title);

        path = [path_origin, ...path_other].join(" • ");
        location = shopZones.value.byId[zone_ids.at(-1)].title;
      }

      item.zones.push({
        id: zone_ids.at(-1),
        path,
        location,
      });
    });

    return item;
  });

  const unsetZone = async () => {
    try {
      const result = await itemUnsetZoneApi({ item_id, zone_ids: state.zone_ids });

      if (result.code === ResponseCode.OK) {
        return true;
      }
    } catch {
      notification.show({ title: t("Ошибка открепления товара"), type: "error" });
    }

    return false;
  };

  const setZone = async () => {
    try {
      const result = await itemSetZoneApi({ item_id: [item_id], zone_id });

      if (result.code === ResponseCode.OK) {
        return true;
      }
    } catch {
      notification.show({ title: t("Ошибка прикрепления товара"), type: "error" });
    }

    return false;
  };

  const handleRelinkProduct = async () => {
    state.is_pending_relink = true;
    const result_unset_zone = await unsetZone();

    if (result_unset_zone) {
      const result_set_zone = await setZone();

      if (result_set_zone) {
        emit("click:link");
      }
    }

    state.is_pending_relink = false;
  };

  const handleLinkItem = async () => {
    state.is_pending_link = true;

    const result_set_zone = await setZone();

    if (result_set_zone) {
      emit("click:link");
    }

    state.is_pending_link = false;
  };

  const loadItem = async () => {
    try {
      const result_item = await itemLoadApi({ item_id: [item_id] });
      const result_item_by_guid = await itemLoadByGuidApi({ guid: result_item.items[0].external_id });

      return {
        item: result_item.items[0],
        zones: result_item_by_guid.zones,
      };
    } catch {
      notification.show({ title: t("Ошибка получения товара"), type: "error" });
    }
  };

  watch(
    () => item_id,
    async () => {
      if (!item_id) return;

      const { item, zones } = await loadItem();

      state.item = item;
      state.zone_ids = zones.map((zone) => zone.zone_id);
    },
  );
</script>

<template>
  <Dialog :isOpen="item_id !== null && item !== null" :closeOnOutsideClick="false" @close="emit('close')">
    <div class="exist-zone-dialog" data-testid="exist-zone-dialog">
      <div class="exist-zone-dialog__content">
        <IconWarningRound style="width: 56px; height: 56px; stroke: var(--sf-base-icon-warning)" />
        <div class="exist-zone-dialog__title">
          <Text font="body-bold-18">{{ $t("Товар уже прикреплен к зоне") }}</Text>
          <Text font="body-regular-14">
            {{ $t("Вы можете перепривязать товар или добавить к текущей зоне, не затрагивая остальные") }}
          </Text>
        </div>
        <ProductZone :zones="item.zones ?? []" :img="item.image_url" :article="item.article" />
      </div>
      <div class="exist-zone-dialog__footer">
        <Button
          size="s"
          @click="handleRelinkProduct"
          :isLoading="state.is_pending_relink"
          data-testid="exist-zone-dialog-relink-btn"
        >
          {{ $t("Перепривязать к текущей") }}
        </Button>
        <Button
          size="s"
          variant="secondary"
          @click="handleLinkItem"
          :isLoading="state.is_pending_link"
          data-testid="exist-zone-dialog-attach-btn"
        >
          {{ $t("Добавить к текущей зоне") }}
        </Button>
        <Button size="s" variant="secondary" @click="emit('close')" data-testid="exist-zone-dialog-cancel-btn">
          {{ $t("Отменить") }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
  .exist-zone-dialog {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    width: 328px;
  }

  .exist-zone-dialog__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .exist-zone-dialog__content :deep(.icon) path {
    stroke-width: 1px;
  }

  .exist-zone-dialog :deep(.product-zone) {
    width: 100%;
    padding: 0;
  }

  .exist-zone-dialog__title {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }

  .exist-zone-dialog__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .exist-zone-dialog__footer :deep(button) {
    width: 100%;
    padding: 0;
  }
</style>
